import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Linkedin, Briefcase, GraduationCap } from "lucide-react";
import TeamPhoto from "@/components/TeamPhoto";

// Modal showing rich team-member details. Adapts to the member's available data
// (rich `detail` block for founders, simple bio + LinkedIn for everyone else).
export default function TeamMemberModal({ member, open, onOpenChange }) {
    if (!member) return null;
    const d = member.detail;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={`team-modal-${member.slug}`}
                className="max-w-3xl bg-background border-brand-border max-h-[90vh] overflow-y-auto p-0"
            >
                {/* Hero band */}
                <div className="relative" style={{ backgroundColor: "#0F1B2E" }}>
                    <div className="px-7 md:px-10 pt-10 pb-7 flex items-center gap-5">
                        <span
                            className="w-20 h-20 rounded-xl overflow-hidden shrink-0 ring-1 ring-brand-amber/30 block"
                            aria-hidden="true"
                        >
                            <TeamPhoto member={member} className="w-full h-full" />
                        </span>
                        <div>
                            <DialogHeader className="space-y-1.5">
                                <DialogTitle className="font-serif text-3xl md:text-4xl text-white leading-tight">
                                    {member.name}
                                </DialogTitle>
                                <p className="overline text-brand-amber">{member.role}</p>
                            </DialogHeader>
                            <a
                                href={member.linkedin}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-3 inline-flex items-center gap-2 text-sm text-white/85 link-underline"
                                data-testid={`team-modal-linkedin-${member.slug}`}
                            >
                                <Linkedin size={15} className="text-brand-amber" /> Connect on LinkedIn
                            </a>
                        </div>
                    </div>
                </div>

                <div className="px-7 md:px-10 py-8 space-y-9">
                    {/* Blurbs */}
                    <section className="space-y-4">
                        {d?.blurbs?.length
                            ? d.blurbs.map((p, i) => (
                                  <p key={i} className="text-brand-ink/80 leading-relaxed text-[15px]">{p}</p>
                              ))
                            : <p className="text-brand-ink/80 leading-relaxed text-[15px]">{member.bio}</p>}
                    </section>

                    {/* Stats */}
                    {d?.stats && (
                        <section>
                            <p className="overline text-brand-amber mb-3">At a glance</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-border rounded-2xl overflow-hidden border border-brand-border">
                                {d.stats.map(s => (
                                    <div key={s.label} className="bg-white p-5 text-center">
                                        <p className="font-serif text-3xl text-brand-ink leading-none">{s.value}</p>
                                        <p className="overline mt-2 text-brand-muted">{s.label}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Career timeline */}
                    {d?.timeline && (
                        <section>
                            <div className="flex items-center gap-2 mb-4">
                                <GraduationCap size={16} className="text-brand-amber" />
                                <p className="overline">Career timeline</p>
                            </div>
                            <ol className="relative border-l-2 border-brand-border pl-6 space-y-5">
                                {d.timeline.map((t, i) => (
                                    <li key={i} className="relative">
                                        <span className="absolute -left-[31px] top-1.5 w-3 h-3 rounded-full bg-brand-amber border-2 border-brand-bg" />
                                        <p className="font-serif text-lg text-brand-ink leading-tight">{t.label}</p>
                                        <p className="text-xs text-brand-muted mt-1">{t.meta}</p>
                                    </li>
                                ))}
                            </ol>
                        </section>
                    )}

                    {/* Practice entities */}
                    {d?.entities && (
                        <section>
                            <p className="overline text-brand-amber mb-3">Practice entities</p>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {d.entities.map(e => (
                                    <div key={e.name} className="card-3d p-5">
                                        <h4 className="font-serif text-xl text-brand-ink leading-tight">{e.name}</h4>
                                        <p className="overline mt-1 text-brand-amber">{e.region}</p>
                                        <p className="text-sm text-brand-ink/75 mt-3 leading-relaxed">{e.body}</p>
                                        <div className="mt-4 flex flex-wrap gap-1.5">
                                            {e.tags.map(t => (
                                                <span key={t} className="badge-soft">{t}</span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Services */}
                    {d?.services && (
                        <section>
                            <div className="flex items-center gap-2 mb-3">
                                <Briefcase size={16} className="text-brand-amber" />
                                <p className="overline">Services</p>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {d.services.map(s => (
                                    <div key={s.title} className="rounded-xl border border-brand-border p-4 bg-brand-creamLight">
                                        <p className="font-semibold text-brand-ink text-sm">{s.title}</p>
                                        <p className="text-xs text-brand-muted mt-1 leading-relaxed">{s.body}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
